import { Box, Typography, useTheme, Button, TextField, Autocomplete } from "@mui/material";
import { useNavigate }  from "react-router-dom";
import { useSelector }  from "react-redux";
import { useState }     from "react";
import moment           from "moment";

import MoneyOffIcon      from "@mui/icons-material/MoneyOff";
import AddIcon           from "@mui/icons-material/Add";
import VisibilityIcon    from "@mui/icons-material/Visibility";
import CancelIcon        from "@mui/icons-material/Cancel";
import ReceiptLongIcon   from "@mui/icons-material/ReceiptLong";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import ListFilterBar from "components/list/ListFilterBar";
import ListDateFilterBar from "components/list/ListDateFilterBar";
import ListSearchField from "components/list/ListSearchField";
import useListDateFilter from "hooks/useListDateFilter";
import FixedSizeDialog   from "components/fixedSizeDialog";
import RefundViewForm    from "../views/refundViewForm";
import { currencyFormatter }     from "utilities/currencyFormatter";
import { useGetRefundsList }     from "api/hooks/billing";
import { requester }             from "api/pesabackend";

export default function RefundsTable({ setIsSuccess }) {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const {
    refundsList, count, isLoadingRefundsList, isLoadingMore, hasMore, loadMore,
    dateFilter, setDateFilter, customRange, setCustomRange, applyCustomRange, setNewReturnData,
  } = useGetRefundsList(1, business_branch_id);

  const [search, setSearch] = useState("");

  const { displayDateFilter, handleFilterChange, handleCustomApply } = useListDateFilter({
    dateFilter, setDateFilter, applyCustomRange, customRange,
  });

  /* record refund dialog */
  const [openRecord,     setOpenRecord]     = useState(false);
  const [receiptList,    setReceiptList]    = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState("");

  /* view dialog */
  const [openView,  setOpenView]  = useState(false);
  const [viewData,  setViewData]  = useState({});

  /* cancel dialog */
  const [openCancel,   setOpenCancel]   = useState(false);
  const [clickedItem,  setClickedItem]  = useState("");

  const filtered = refundsList.filter((r) =>
    r.related_receipt?.receipt_number?.toLowerCase().includes(search.toLowerCase())
  );

  const fetchReceiptList = async (searchTerm) => {
    try {
      const url = `/billing/receipts?search=False&business_branch_id=${business_branch_id}`;
      const res = await requester.get(searchTerm ? `${url}&search=${searchTerm}` : url);
      setReceiptList(res.data.data);
    } catch {}
  };

  const handleOpenReturn = async (invoiceId) => {
    const res = await requester.get(`/billing/invoices/meta_details?id=${invoiceId}`);
    if (res.status === 200) {
      navigate("/refundForm", { state: { id: invoiceId, data: res.data.data } });
    }
  };

  const handleClickView = async (id) => {
    const res = await requester.get(`/billing/refunds/details?refund_id=${id}`);
    if (res.status === 200) { setViewData(res.data.data); setOpenView(true); }
  };

  const handleClickDeleteOpen = async (id) => {
    const res = await requester.get(`/inventory/purchase_return/get_form/${id}`);
    if (res.status === 200) { setClickedItem(id); setOpenCancel(true); }
  };

  const handleClickDelete = async () => {
    const res = await requester.post(`/inventory/purchase_return/cancel/${clickedItem}`);
    if (res.status === 201) {
      setIsSuccess(true);
      setNewReturnData(true);
      setOpenCancel(false);
    }
  };

  const columns = [
    { label: "Refund Date"    },
    { label: "Receipt No."   },
    { label: "Customer"      },
    { label: "Total Refund", align: "right" },
    { label: "Created By"   },
    { label: "Actions",     align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{
            width: "40px", height: "40px", borderRadius: "10px",
            backgroundColor: "rgba(29,158,117,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent, flexShrink: 0,
          }}>
            <MoneyOffIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Refunds
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
              {count} total records
            </Typography>
          </Box>
        </Box>

        <Button
          onClick={() => { fetchReceiptList(null); setOpenRecord(true); }}
          startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
          sx={{
            fontSize: "13px", fontWeight: 500,
            px: "1rem", py: "0.45rem", borderRadius: "8px",
            backgroundColor: accent, color: "#fff", textTransform: "none",
            "&:hover": { backgroundColor: "#0F6E56" },
          }}
        >
          Record Refund
        </Button>
      </Box>

      <ListFilterBar>
        <ListDateFilterBar
          embedded
          dateFilter={displayDateFilter}
          customRange={customRange}
          onFilterChange={handleFilterChange}
          onCustomRangeChange={setCustomRange}
          onCustomApply={handleCustomApply}
        />
        <ListSearchField placeholder="Search receipt number…" onSearch={setSearch} />
      </ListFilterBar>

      {/* ── Table ── */}
      <ReportTable
        columns={columns}
        rows={filtered}
        loading={isLoadingRefundsList}
        hasMore={hasMore}
        onLoadMore={loadMore}
        loadingMore={isLoadingMore}
        emptyMessage="No refunds found for this period"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{moment(row.date_created).format("LL")}</RCell>
            <RCell>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>
                {row.related_receipt.receipt_number}
              </Typography>
            </RCell>
            <RCell>
              {row.related_receipt.related_invoice.related_customer
                ? `${row.related_receipt.related_invoice.related_customer.related_user.first_name} ${row.related_receipt.related_invoice.related_customer.related_user.last_name}`
                : <Typography sx={{ fontSize: "12px", color: textSec, fontStyle: "italic" }}>Walk-in customer</Typography>
              }
            </RCell>
            <RCell align="right">
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>
                {currencyFormatter(parseFloat(row.refunded_amount))}
              </Typography>
            </RCell>
            <RCell>{`${row.created_by.first_name} ${row.created_by.last_name}`}</RCell>
            <RCell align="center">
              <Box sx={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                <Box
                  onClick={() => handleClickView(row.id)}
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: "4px",
                    px: "8px", py: "4px", borderRadius: "6px",
                    border: "1px solid rgba(29,158,117,0.3)",
                    cursor: "pointer", color: accent,
                    fontSize: "12px", fontWeight: 500,
                    transition: "all 0.15s",
                    "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" },
                  }}
                >
                  <VisibilityIcon sx={{ fontSize: "14px" }} /> View
                </Box>
                <Box
                  onClick={() => handleClickDeleteOpen(row.id)}
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: "4px",
                    px: "8px", py: "4px", borderRadius: "6px",
                    border: "1px solid rgba(226,75,74,0.3)",
                    cursor: "pointer", color: "#e24b4a",
                    fontSize: "12px", fontWeight: 500,
                    transition: "all 0.15s",
                    "&:hover": { backgroundColor: "rgba(226,75,74,0.08)" },
                  }}
                >
                  <CancelIcon sx={{ fontSize: "14px" }} /> Cancel
                </Box>
              </Box>
            </RCell>
          </RRow>
        )}
      />

      {/* ── Record refund dialog ── */}
      <FixedSizeDialog
        open={openRecord}
        handleClose={() => { setOpenRecord(false); setSelectedInvoice(""); }}
        dialogHeading="Record a Refund"
        negativeResponse="Cancel"
        maxWidth="sm"
        scroll="body"
        content={
          <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {/* Info banner */}
            <Box sx={{
              display: "flex", alignItems: "flex-start", gap: "10px",
              p: "0.875rem 1rem",
              backgroundColor: "rgba(29,158,117,0.07)",
              border: `1px solid rgba(29,158,117,0.2)`,
              borderRadius: "8px",
            }}>
              <ReceiptLongIcon sx={{ fontSize: "18px", color: accent, flexShrink: 0, mt: "1px" }} />
              <Typography sx={{ fontSize: "13px", color: isDark ? "#9FE1CB" : "#0F6E56", lineHeight: 1.6 }}>
                Search for the receipt number associated with the refund you want to record.
              </Typography>
            </Box>

            <Autocomplete
              onOpen={() => fetchReceiptList(null)}
              onChange={(_, value) => setSelectedInvoice(value ? value.related_invoice.id : "")}
              options={receiptList}
              getOptionLabel={(o) =>
                `${o.receipt_number} — ${o.related_invoice.related_customer
                  ? `${o.related_invoice.related_customer.related_user.first_name} ${o.related_invoice.related_customer.related_user.last_name}`
                  : "Walk-in customer"}`
              }
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderOption={(props, o) => (
                <Box component="li" {...props} key={o.id}>
                  <Box>
                    <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{o.receipt_number}</Typography>
                    <Typography sx={{ fontSize: "11px", color: textSec }}>
                      {o.related_invoice.related_customer
                        ? `${o.related_invoice.related_customer.related_user.first_name} ${o.related_invoice.related_customer.related_user.last_name}`
                        : "Walk-in customer"}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Receipt number"
                  onChange={(e) => fetchReceiptList(e.currentTarget.value)}
                  sx={{
                    "& .MuiInputBase-root": { fontSize: "13px", borderRadius: "8px" },
                    "& .MuiInputLabel-root": { fontSize: "13px" },
                  }}
                />
              )}
            />

            <Button
              fullWidth
              disabled={!selectedInvoice}
              onClick={() => handleOpenReturn(selectedInvoice)}
              sx={{
                fontSize: "13px", fontWeight: 500, py: "0.6rem",
                borderRadius: "8px", textTransform: "none",
                backgroundColor: accent, color: "#fff",
                "&:hover": { backgroundColor: "#0F6E56" },
                "&:disabled": { opacity: 0.5 },
              }}
            >
              Continue to Refund Form
            </Button>
          </Box>
        }
      />

      {/* ── View dialog ── */}
      <FixedSizeDialog
        open={openView}
        handleClose={() => setOpenView(false)}
        dialogHeading="Refund Details"
        negativeResponse="Close"
        maxWidth="lg"
        scroll="body"
        content={<RefundViewForm viewData={viewData} />}
      />

      {/* ── Cancel dialog ── */}
      <FixedSizeDialog
        open={openCancel}
        handleClose={() => setOpenCancel(false)}
        dialogHeading="Cancel this refund?"
        negativeResponse="Go back"
        positiveResponse="Yes, cancel refund"
        handleSubmit={handleClickDelete}
        maxWidth="sm"
        content={
          <Box sx={{
            display: "flex", alignItems: "flex-start", gap: "10px",
            p: "0.875rem 1rem",
            backgroundColor: "rgba(226,75,74,0.07)",
            border: "1px solid rgba(226,75,74,0.2)",
            borderRadius: "8px",
          }}>
            <CancelIcon sx={{ fontSize: "18px", color: "#e24b4a", flexShrink: 0, mt: "1px" }} />
            <Typography sx={{ fontSize: "13px", color: isDark ? "#f09595" : "#a32d2d", lineHeight: 1.6 }}>
              This action will cancel the refund and cannot be undone. Are you sure you want to proceed?
            </Typography>
          </Box>
        }
      />
    </Box>
  );
}